import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTutorial } from "../context/TutorialContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Sparkles, ArrowLeft, Globe } from "lucide-react";

/**
 * /onboarding — welcome landing for new users.
 *
 * This page used to be a 7-step brochure flow. It's been replaced by
 * a single welcome card that **launches the live spotlight tour**
 * (<TutorialOverlay />). The tour itself runs on top of the actual
 * app (dashboard, navbar, floating bubbles) instead of static
 * marketing screens — the user learns the UI by seeing it.
 *
 *   • First visit  →  auto-shows (route landed here from /auth)
 *   • Settings →  `?force=1` bypasses the "already done" redirect
 *   • Closing of tour  →  persists `onboarding_completed` and lands
 *                          the user on /library
 *
 * "Skip" closes the welcome WITHOUT starting the tour and persists the
 * flag, so a user who wants to dive straight in never gets blocked.
 */
export default function Onboarding() {
  const { user, loading, completeOnboarding } = useAuth();
  const { start: startTour } = useTutorial();
  const { lang, setLang, isEn } = useLanguage();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const forced = search.get("force") === "1";

  // The language picker is the very first onboarding step. We skip it
  // when the user has already explicitly chosen — detected by the
  // presence of `torahya_lang` in localStorage. Forcing via ?force=1
  // always shows it again so users can re-pick from /settings → tour.
  const langPicked = (() => {
    if (forced) return false;
    if (typeof window === "undefined") return true;
    try { return !!window.localStorage.getItem("torahya_lang"); }
    catch { return true; }
  })();
  const [step, setStep] = useState(langPicked ? "welcome" : "language");

  // First-name extraction for the welcome.
  const userName = useMemo(() => {
    if (!user?.name) return "";
    return String(user.name).trim().split(/\s+/)[0];
  }, [user]);

  const chooseLanguage = (next) => {
    setLang(next);
    // Move to the welcome step. The LanguageContext effect flips the
    // <html dir> attribute synchronously so the next render is already
    // in the right reading direction.
    setStep("welcome");
  };

  // Auto-redirect rules. Same shape as the previous version so the
  // contract with /auth → /onboarding doesn't change.
  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/auth", { replace: true });
      return;
    }
    if (user.onboarding_completed && !forced) {
      navigate("/library", { replace: true });
    }
  }, [user, loading, navigate, forced]);

  const handleStart = () => {
    // Kick off the live tour. The overlay sits on top of the actual
    // dashboard so the first step navigates the user to /beit-midrash
    // and begins highlighting elements from there.
    startTour();
    navigate("/beit-midrash");
  };

  const handleSkip = async () => {
    try {
      await completeOnboarding();
    } finally {
      navigate("/library", { replace: true });
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-10 w-10 rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
      </div>
    );
  }

  return (
    <div
      data-testid="onboarding-page-v2"
      className="relative min-h-screen w-full"
      dir={isEn ? "ltr" : "rtl"}
    >
      {/* Reuse the /auth stage so the visual thread "auth → welcome →
          tour" stays unbroken. */}
      <div className="auth-stage" aria-hidden="true" />
      <div className="auth-dust" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => <span key={i} />)}
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-lg items-center justify-center px-4 sm:px-6 py-12">
        {step === "language" ? (
          /* Step 1 — language picker. Shown to genuinely new users so
             the rest of the onboarding speaks to them in their chosen
             tongue. The choice is persisted via LanguageContext. */
          <div
            className="auth-card auth-enter w-full px-6 sm:px-9 py-9 sm:py-11"
            data-testid="onboarding-step-language"
          >
            <p className="auth-enter auth-enter-d1 text-center font-latin italic text-[10px] tracking-[0.45em] uppercase text-gold/85">
              תוריא · Torahya
            </p>
            <div className="auth-enter auth-enter-d2 mt-5 flex justify-center" aria-hidden>
              <span className="auth-brand-halo">
                <span className="h-16 w-16 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shadow-inner">
                  <Globe className="h-7 w-7 text-gold" strokeWidth={1.5} />
                </span>
              </span>
            </div>
            <h1
              dir="rtl"
              className="auth-enter auth-enter-d3 mt-5 font-heading text-2xl sm:text-3xl text-foreground leading-snug text-center"
              data-testid="onboarding-language-title"
            >
              באיזו שפה תרצה ללמוד?
            </h1>
            <h2
              dir="ltr"
              className="mt-2 font-heading text-xl sm:text-2xl text-foreground/85 leading-snug text-center"
            >
              In which language would you like to learn?
            </h2>
            <div className="auth-enter auth-enter-d5 mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => chooseLanguage("he")}
                data-testid="onboarding-choose-hebrew"
                dir="rtl"
                className={`group relative rounded-sm border px-5 py-5 text-center transition-all
                  ${lang === "he"
                    ? "border-gold bg-gold/10 ring-1 ring-gold/40"
                    : "border-gold/30 hover:border-gold/60 hover:bg-gold/5"}`}
              >
                <span className="block font-heading text-2xl text-foreground mb-1">עברית</span>
                <span className="block font-latin italic text-[11px] text-muted-foreground tracking-widest uppercase">
                  Hebrew · ברירת מחדל
                </span>
              </button>
              <button
                type="button"
                onClick={() => chooseLanguage("en")}
                data-testid="onboarding-choose-english"
                dir="ltr"
                className={`group relative rounded-sm border px-5 py-5 text-center transition-all
                  ${lang === "en"
                    ? "border-gold bg-gold/10 ring-1 ring-gold/40"
                    : "border-gold/30 hover:border-gold/60 hover:bg-gold/5"}`}
              >
                <span className="block font-heading text-2xl text-foreground mb-1">English</span>
                <span className="block font-latin italic text-[11px] text-muted-foreground tracking-widest uppercase">
                  for the whole Jewish world
                </span>
              </button>
            </div>
            <p className="mt-5 text-center text-[11px] text-muted-foreground/80 italic">
              {isEn
                ? "You can change this any time from Settings."
                : "אפשר לשנות בכל עת בהגדרות."}
            </p>
          </div>
        ) : (
        <div
          className="auth-card auth-enter w-full px-6 sm:px-9 py-9 sm:py-11"
          data-testid="onboarding-step-welcome"
        >
          {/* Eyebrow */}
          <p className="auth-enter auth-enter-d1 text-center font-latin italic text-[10px] tracking-[0.45em] uppercase text-gold/85">
            תוריא · Torahya
          </p>

          {/* Sparkle halo */}
          <div className="auth-enter auth-enter-d2 mt-5 flex justify-center" aria-hidden>
            <span className="auth-brand-halo">
              <span className="h-16 w-16 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shadow-inner">
                <Sparkles className="h-7 w-7 text-gold" strokeWidth={1.5} />
              </span>
            </span>
          </div>

          {/* Title */}
          <h1
            className="auth-enter auth-enter-d3 mt-5 font-heading text-3xl sm:text-4xl text-foreground leading-tight text-center"
            data-testid="onboarding-title"
          >
            {isEn ? (
              userName
                ? <>Welcome,<br/><span className="text-gold">{userName}</span></>
                : <>Welcome<br/><span className="text-gold">to Torahya</span></>
            ) : (
              userName
                ? <>ברוכים הבאים,<br/><span className="text-gold">{userName}</span></>
                : <>ברוכים הבאים<br/><span className="text-gold">לתוריא</span></>
            )}
          </h1>

          {/* Body */}
          <p
            className="auth-enter auth-enter-d4 mt-5 text-sm sm:text-base text-muted-foreground leading-relaxed text-center max-w-md mx-auto"
            data-testid="onboarding-body"
          >
            {isEn
              ? "Your personal Beit Midrash — texts, chavruta, and a study basket. Take a quick tour and we'll show you where everything is ✨"
              : "בית המדרש האישי שלך — ספרים, חברותא, וסל לימוד. בוא נצא לסיור קצר ונראה לך איפה כל דבר נמצא ✨"}
          </p>

          {/* Verse — kept in Hebrew always; English caption underneath
              when the UI is in English so it speaks to every learner. */}
          <div className="auth-enter auth-enter-d5 mt-5 text-center">
            <div className="ornamental-divider !my-3 text-gold/55">✦</div>
            <p
              className="auth-verse font-heading text-base sm:text-lg text-foreground/85 leading-relaxed"
              dir="rtl"
            >
              וְהָגִיתָ בּוֹ יוֹמָם וָלָיְלָה
            </p>
            <p className="font-latin italic text-[10px] tracking-[0.3em] uppercase text-gold/70 mt-1">
              {isEn ? "Joshua 1:8 · “meditate on it day and night”" : "יהושע א׳"}
            </p>
          </div>

          {/* CTAs */}
          <div className="auth-enter auth-enter-d6 mt-7 flex flex-col gap-3 items-center">
            <Button
              onClick={handleStart}
              data-testid="onboarding-start-tour"
              className="sacred-btn-gold text-base px-7 py-5 min-h-[52px] min-w-[240px] inline-flex items-center justify-center gap-2"
            >
              {isEn ? "Let’s begin" : "יאללה, בוא נתחיל"}
              <ArrowLeft className={`h-4 w-4 ${isEn ? "rotate-180" : ""}`} strokeWidth={1.75} />
            </Button>
            <button
              type="button"
              onClick={handleSkip}
              data-testid="onboarding-skip"
              className="text-xs sm:text-sm text-muted-foreground hover:text-foreground py-2 px-3 rounded-sm hover:bg-card/60 font-heading transition-colors"
            >
              {isEn ? "Skip the tour" : "דלג על הסיור"}
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
